/**
 * Shared formatters for the opus-ia frontend.
 *
 * ALL formatting of USD, token counts, dates, and percentages should
 * import from this module -- no local re-implementations with
 * conflicting locales.
 *
 * Locale: pt-BR everywhere (matches the app default).
 */

// ---------------------------------------------------------------------------
// Currency
// ---------------------------------------------------------------------------

/**
 * Format USD value with pt-BR locale. Always 2 decimal places.
 *
 * @example formatUsd(1234.5) // "US$ 1.234,50"
 */
export function formatUsd(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

/**
 * Format USD value with a given number of decimal places (prices per 1M tokens).
 *
 * @example formatUsdPrecise(3.75)      // "US$ 3,7500"
 * @example formatUsdPrecise(0.1, 6)    // "US$ 0,100000"
 */
export function formatUsdPrecise(value: number, digits = 4): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

// ---------------------------------------------------------------------------
// Token counts
// ---------------------------------------------------------------------------

/**
 * Format token count with compact notation for large numbers.
 *
 * @example
 * formatTokenCount(500)       // "500"
 * formatTokenCount(1_500)     // "1.5K"
 * formatTokenCount(2_300_000) // "2.3M"
 */
export function formatTokenCount(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}M`;
  if (value >= 1_000) return `${(value / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}K`;
  return value.toLocaleString('pt-BR');
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/**
 * Format date in pt-BR style.
 *
 * @param date  - ISO string or Date object
 * @param style - 'short' for "12/06/2026", 'long' for "12 de jun. de 2026",
 *                'compact' for "12/06" (day/month only, useful for chart axes)
 *
 * @example
 * formatDate('2026-06-12')             // "12/06/2026"
 * formatDate('2026-06-12', 'long')     // "12 de jun. de 2026"
 * formatDate('2026-06-12', 'compact')  // "12/06"
 */
export function formatDate(
  date: string | Date,
  style: 'short' | 'long' | 'compact' = 'short',
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (style === 'compact') {
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }
  return style === 'short'
    ? d.toLocaleDateString('pt-BR')
    : d.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
}

/** Organization time zone (locked decision: BRT). */
export const ORG_TIME_ZONE = 'America/Sao_Paulo';

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;

/** True when the string is an ISO 8601 timestamp (date and time). */
export function isIsoTimestamp(value: unknown): value is string {
  return typeof value === 'string' && ISO_TIMESTAMP.test(value);
}

/**
 * Format an ISO timestamp as a calendar day in the organization time zone (locale defaults to pt-BR).
 *
 * @example
 * formatDayInOrgTz('2026-10-06T03:00:00.000Z') // "06/10/2026"
 */
// --- FEAT-052 TASK-023 (locale) ---
export function formatDayInOrgTz(iso: string | Date, locale = 'pt-BR'): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { timeZone: ORG_TIME_ZONE });
}

// ---------------------------------------------------------------------------
// Decimal numbers
// ---------------------------------------------------------------------------

/**
 * Format a decimal number with pt-BR locale.
 *
 * @param value - the number to format
 * @param fractionDigits - exact number of decimal places (default 2)
 * @param locale - BCP 47 locale (default 'pt-BR')
 *
 * @example
 * formatDecimal(3.1415, 2) // "3,14"
 * formatDecimal(1000.5, 1) // "1.000,5"
 */
export function formatDecimal(value: number, fractionDigits = 2, locale = 'pt-BR'): string { // FEAT-052 TASK-006: locale opcional
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

// ---------------------------------------------------------------------------
// Percentages
// ---------------------------------------------------------------------------

/**
 * Format percentage with 1 decimal place using pt-BR locale.
 *
 * @example formatPercent(85.3333) // "85,3%"
 * @example formatPercent(85.3333, 'en-US') // "85.3%"
 */
export function formatPercent(value: number, locale = 'pt-BR'): string { // FEAT-052 TASK-006: locale opcional
  return `${formatDecimal(value, 1, locale)}%`;
}
